<template>
  <v-menu
    v-model="menuOpen"
    location="bottom start"
    :close-on-content-click="false"
    transition="scale-transition"
    @update:model-value="onMenuToggle"
  >
    <template #activator="{ props: activatorProps }">
      <v-btn
        v-bind="activatorProps"
        variant="outlined"
        rounded="lg"
        class="filter-style-btn"
      >
        <v-icon icon="mdi-account-plus-outline" start />
        Add Draft Profile
      </v-btn>
    </template>
    <v-card min-width="300" max-height="380" class="dropdown-card">
      <div class="pa-3 pb-0">
        <v-text-field
          v-model="searchText"
          density="compact"
          variant="outlined"
          flat
          hide-details
          placeholder="Search draft profiles…"
          autofocus
          color="brandPrimary"
          base-color="textDarkest"
          @keydown.escape="menuOpen = false"
        />
      </div>
      <v-list class="py-0 dropdown-list" density="compact">
        <template v-if="!searchText.trim()">
          <v-list-item disabled>
            <span class="text-body-2 text-medium-emphasis">Type to search profiles</span>
          </v-list-item>
        </template>
        <template v-else-if="isSearching">
          <v-list-item disabled>
            <span class="text-body-2 text-medium-emphasis">Searching…</span>
          </v-list-item>
        </template>
        <template v-else-if="filteredProfiles.length === 0">
          <v-list-item disabled>
            <span class="text-body-2 text-medium-emphasis">No matching profiles</span>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item
            v-for="item in filteredProfiles"
            :key="item.ulid"
            class="dropdown-list-item"
            @click="handleSelect(item.ulid)"
          >
            <div class="dropdown-profile-row d-flex align-end flex-wrap">
              <span class="text-body-2 item-label">{{ item.title }}</span>
              <span
                v-if="item.jobCode"
                class="job-architecture-job-code text-body-3 ml-2"
              >
                {{ item.jobCode }}
              </span>
            </div>
          </v-list-item>
        </template>
      </v-list>
    </v-card>
  </v-menu>
</template>

<script setup>
import {
  ref, computed, watch,
} from 'vue';
import { getJobProfileDrafts } from '@/api/jobProfile';

const props = defineProps({
  existingUlids: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['add-profile']);

const menuOpen = ref(false);
const searchText = ref('');
const isSearching = ref(false);
const searchResults = ref([]);

const existingSet = computed(() => new Set(props.existingUlids || []));

const filteredProfiles = computed(() => searchResults.value
  .filter((profile) => profile.ulid && !existingSet.value.has(profile.ulid))
  .map((profile) => ({
    ulid: profile.ulid,
    title: profile.title || profile.ulid,
    jobCode: profile.job_code,
  })));

let searchTimeout = null;

const runSearch = async (query) => {
  const trimmed = query.trim();

  if (!trimmed) {
    searchResults.value = [];
    isSearching.value = false;

    return;
  }

  isSearching.value = true;

  try {
    const { documents = [] } = await getJobProfileDrafts({
      mode: 'draft',
      params: {
        sort: 'title',
        page: 1,
        size: 100,
      },
      searchTerm: trimmed,
    });

    if (searchText.value.trim() === trimmed) {
      searchResults.value = documents;
    }
  } catch {
    if (searchText.value.trim() === trimmed) {
      searchResults.value = [];
    }
  } finally {
    if (searchText.value.trim() === trimmed) {
      isSearching.value = false;
    }
  }
};

watch(searchText, (value) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (!value.trim()) {
    searchResults.value = [];
    isSearching.value = false;

    return;
  }

  isSearching.value = true;
  searchTimeout = setTimeout(() => {
    runSearch(value);
  }, 300);
});

const onMenuToggle = (open) => {
  if (!open) {
    searchText.value = '';
    searchResults.value = [];
    isSearching.value = false;

    if (searchTimeout) {
      clearTimeout(searchTimeout);
      searchTimeout = null;
    }
  }
};

const handleSelect = (ulid) => {
  emit('add-profile', ulid);

  menuOpen.value = false;
  searchText.value = '';
  searchResults.value = [];
};
</script>

<style scoped lang="less">
.filter-style-btn {
  height: 40px !important;
  min-height: 40px !important;
  background-color: @bg-default !important;
  border: 1px solid @stroke-default;
  color: @text-darkest;
  font-weight: 400;
  text-transform: none;
}

.dropdown-card {
  border: 0 !important;
  box-shadow: 0 10px 50px 0 fade(@grey-900, 10%) !important;
  border-radius: 8px;
}

.dropdown-list {
  max-height: 280px;
  overflow-y: auto;
  border-radius: 0 0 8px 8px;
}

.dropdown-list-item {
  min-height: 40px;
  padding-left: 28px;
  cursor: pointer;
}

.dropdown-profile-row {
  width: 100%;
}

.job-architecture-job-code {
  color: @text-lighter;
  line-height: 135% !important;
}

.item-label {
  margin-left: 8px;
  color: fade(@grey-700, 95%);
}

.dropdown-list-item:hover .item-label {
  color: @text-brand;
}
</style>
